import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import http from '../api/http';
import { Calendar, Plus, History, RefreshCw, ChevronLeft, ChevronRight, X, Filter } from 'lucide-react';
import BlueHeader from '../components/BlueHeader';

const GestionSoldesConge = () => {
  const { user } = useAuth();
  const { filialeId, isConsolidated } = useFiliale();
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const [soldeFilter, setSoldeFilter] = useState({ operateur: '', valeur: '' });
  
  // États pour les modales
  const [showAjoutModal, setShowAjoutModal] = useState(false);
  const [showAjustementModal, setShowAjustementModal] = useState(false);
  const [showHistoriqueModal, setShowHistoriqueModal] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [historique, setHistorique] = useState([]);
  
  const [formData, setFormData] = useState({
    nbre_jour: '',
    motif: ''
  });
  const [ajustementData, setAjustementData] = useState({
    nouveau_solde: '',
    motif: ''
  });

  useEffect(() => {
    setPage(1);
  }, [soldeFilter, filialeId, isConsolidated]);

  useEffect(() => {
    fetchPersonnel();
  }, [page, soldeFilter, filialeId, isConsolidated]);

  const fetchPersonnel = async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (!isConsolidated && filialeId) {
        params.filiale_id = filialeId;
      }
      if (soldeFilter.operateur && soldeFilter.valeur !== '') {
        params.solde_operateur = soldeFilter.operateur;
        params.solde_valeur = soldeFilter.valeur;
      }
      const response = await http.get('/api/personnel', { params });
      const data = response.data?.data || [];
      setPersonnel(Array.isArray(data) ? data : []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      console.error('Erreur récupération personnel:', error);
      setPersonnel([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistorique = async (personnelId) => {
    try {
      const response = await http.get(`/api/solde-conge/historique/${personnelId}`);
      setHistorique(response.data || []);
    } catch (error) {
      console.error('Erreur récupération historique:', error);
      setHistorique([]);
    }
  };

  const handleAjout = async (e) => {
    e.preventDefault();
    try {
      await http.post('/api/solde-conge/ajouter', {
        idpersonnel: selectedPersonnel.idpersonnel,
        nbre_jour: formData.nbre_jour,
        motif: formData.motif
      });
      alert('Jours ajoutés avec succès');
      setShowAjoutModal(false);
      setFormData({ nbre_jour: '', motif: '' });
      fetchPersonnel();
    } catch (error) {
      console.error('Erreur ajout:', error);
      alert('Erreur lors de l\'ajout');
    }
  };

  const handleAjustement = async (e) => {
    e.preventDefault();
    try {
      await http.post('/api/solde-conge/ajuster', {
        idpersonnel: selectedPersonnel.idpersonnel,
        nouveau_solde: ajustementData.nouveau_solde,
        motif: ajustementData.motif
      });
      alert('Solde ajusté avec succès');
      setShowAjustementModal(false);
      setAjustementData({ nouveau_solde: '', motif: '' });
      fetchPersonnel();
    } catch (error) {
      console.error('Erreur ajustement:', error);
      alert('Erreur lors de l\'ajustement');
    }
  };

  const openAjoutModal = (p) => {
    setSelectedPersonnel(p);
    setFormData({ nbre_jour: '', motif: '' });
    setShowAjoutModal(true);
  };

  const openAjustementModal = (p) => {
    setSelectedPersonnel(p);
    setAjustementData({ nouveau_solde: p.solde_conge, motif: '' });
    setShowAjustementModal(true);
  };

  const openHistoriqueModal = async (p) => {
    setSelectedPersonnel(p);
    await fetchHistorique(p.idpersonnel);
    setShowHistoriqueModal(true);
  };

  const getActionBadge = (action) => {
    const badges = {
      'attribution_mensuelle': 'bg-green-100 text-green-600',
      'ajout_manuel': 'bg-sky-100 text-sky-600',
      'consommation': 'bg-red-100 text-red-600',
      'annulation': 'bg-yellow-100 text-yellow-600',
      'ajustement': 'bg-purple-100 text-purple-600'
    };
    return badges[action] || 'bg-gray-100 text-gray-600';
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Gestion des Soldes de Congé"
        subtitle="Gérez les soldes de congé des employés"
        actions={[
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchPersonnel,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8 space-y-6">
      {/* Filtre solde */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-3.5 w-3.5 text-sky-700" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Filtre solde de congé
              </span>
            </div>
            <div className="flex gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                  Opérateur
                </label>
                <select
                  value={soldeFilter.operateur}
                  onChange={(e) => setSoldeFilter({ ...soldeFilter, operateur: e.target.value })}
                  className="w-40 h-10 bg-slate-50 border border-slate-200 rounded-lg px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 transition-colors"
                >
                  <option value="">Tous</option>
                  <option value=">">Supérieur à</option>
                  <option value="<">Inférieur à</option>
                  <option value="=">Égal à</option>
                  <option value=">=">Supérieur ou égal</option>
                  <option value="<=">Inférieur ou égal</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                  Valeur (jours)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={soldeFilter.valeur}
                  onChange={(e) => setSoldeFilter({ ...soldeFilter, valeur: e.target.value })}
                  className="w-40 h-10 bg-slate-50 border border-slate-200 rounded-lg px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 transition-colors"
                  placeholder="Nombre de jours"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setSoldeFilter({ operateur: '', valeur: '' })}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:border-sky-500 hover:text-sky-700 transition-colors"
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lien Report Annuel */}
      <div className="flex justify-end">
        <button
          onClick={() => window.location.href = '/report-annuel'}
          className="px-4 py-2 bg-gradient-to-r from-[#2563eb] to-[#3b82f6] text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-blue-500/25 flex items-center gap-2"
        >
          <Calendar className="h-4 w-4" />
          Report Annuel
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex h-48 items-center justify-center text-sm text-slate-500">
            Chargement…
          </div>
        ) : personnel.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-slate-500">
            Aucun employé.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-sky-50/60">
                <tr>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Employé</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Matricule</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Fonction</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Solde actuel</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {personnel.map((p) => (
                  <tr key={p.idpersonnel} className="hover:bg-sky-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 font-medium">
                      {p.prenom} {p.nom}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {p.matricule}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {p.titre_nom || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${p.solde_conge >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                        {p.solde_conge} jours
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button type="button" onClick={() => openAjoutModal(p)} className="text-sky-600 hover:text-sky-800" title="Ajouter des jours">
                          <Plus className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => openAjustementModal(p)} className="text-purple-600 hover:text-purple-800" title="Ajuster le solde">
                          <Calendar className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => openHistoriqueModal(p)} className="text-slate-600 hover:text-slate-800" title="Voir l'historique">
                          <History className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
            <div className="text-sm text-slate-500">
              Page {page} sur {totalPages} ({total} éléments)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded border border-slate-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded border border-slate-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Ajout */}
      {showAjoutModal && selectedPersonnel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-sky-100">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">Ajouter des jours</h3>
              <button type="button" onClick={() => setShowAjoutModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4 bg-sky-50 p-3 rounded-lg">
              <div className="font-medium text-slate-900">{selectedPersonnel.prenom} {selectedPersonnel.nom}</div>
              <div className="text-sm text-slate-600">Solde actuel: {selectedPersonnel.solde_conge} jours</div>
            </div>
            <form onSubmit={handleAjout} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Nombre de jours</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.nbre_jour}
                  onChange={(e) => setFormData({ ...formData, nbre_jour: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:border-sky-500 focus:ring-sky-500"
                  required
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Motif</label>
                <textarea
                  value={formData.motif}
                  onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                  rows="2"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:border-sky-500 focus:ring-sky-500"
                  placeholder="Motif de l'ajout"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
                <button type="button" onClick={() => setShowAjoutModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
                  Annuler
                </button>
                <button type="submit" className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700">
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Ajustement */}
      {showAjustementModal && selectedPersonnel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-purple-100">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">Ajuster le solde</h3>
              <button type="button" onClick={() => setShowAjustementModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4 bg-purple-50 p-3 rounded-lg">
              <div className="font-medium text-slate-900">{selectedPersonnel.prenom} {selectedPersonnel.nom}</div>
              <div className="text-sm text-slate-600">Solde actuel: {selectedPersonnel.solde_conge} jours</div>
            </div>
            <form onSubmit={handleAjustement} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Nouveau solde</label>
                <input
                  type="number"
                  step="0.5"
                  value={ajustementData.nouveau_solde}
                  onChange={(e) => setAjustementData({ ...ajustementData, nouveau_solde: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:border-purple-500 focus:ring-purple-500"
                  required
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Motif</label>
                <textarea
                  value={ajustementData.motif}
                  onChange={(e) => setAjustementData({ ...ajustementData, motif: e.target.value })}
                  rows="2"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:border-purple-500 focus:ring-purple-500"
                  placeholder="Motif de l'ajustement"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
                <button type="button" onClick={() => setShowAjustementModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
                  Annuler
                </button>
                <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  Ajuster
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Historique */}
      {showHistoriqueModal && selectedPersonnel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl border border-slate-200 max-h-[80vh] overflow-y-auto">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">
                <History className="h-5 w-5 inline mr-2" />
                Historique - {selectedPersonnel.prenom} {selectedPersonnel.nom}
              </h3>
              <button type="button" onClick={() => setShowHistoriqueModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-sky-50/60">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase">Date</th>
                    <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase">Action</th>
                    <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase">Ancien solde</th>
                    <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase">Nouveau solde</th>
                    <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase">Différence</th>
                    <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase">Motif</th>
                    <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase">Exécuté par</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {historique.map((h) => (
                    <tr key={h.id} className="hover:bg-sky-50/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {new Date(h.date_action).toLocaleString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getActionBadge(h.action)}`}>
                          {h.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{h.ancien_solde}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{h.nouveau_solde}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <span className={h.difference >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {h.difference >= 0 ? '+' : ''}{h.difference}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">{h.motif}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{h.execute_par}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {historique.length === 0 && (
                <div className="text-center py-8 text-slate-500">Aucun historique disponible</div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default GestionSoldesConge;
